import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {BehaviorSubject, EMPTY} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  tap
} from 'rxjs/operators';

import {RmeNodeDaoService} from '../../../../../../../../../core/services/rme-node-dao.service';

import {
  constants,
  RefModelStoreService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {SearchErrorStateMatcher} from '../../../../../../../create-node-dialog/node-error-state-matcher';
import {SearchResult} from '../../models/search-result.interface';

const NODES_LIMIT = 10;
const PARENTS_LIMIT = 10;

@Component({
  selector: 'app-mini-node-search',
  templateUrl: './mini-node-search.component.html',
  styleUrls: ['./mini-node-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MiniNodeSearchComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnChanges
{
  @Input() column: string;
  @Input() addedParents: any[];

  @Output() addParentNode = new EventEmitter<any>();
  @Output() removeParentNode = new EventEmitter<any>();

  searchFormControl: UntypedFormControl;
  matcher: SearchErrorStateMatcher;
  nodeList: any[];
  private result: any;
  loading$ = new BehaviorSubject<boolean>(false);
  disableSearch: boolean;

  constructor(
    private rmeStore: RefModelStoreService,
    private rmeNodeDaoService: RmeNodeDaoService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.searchFormControl = new UntypedFormControl();

    this._changeInputState();
    this.searchProcessObserver();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.addedParents?.currentValue) {
      this._changeInputState();
    }
  }

  clearSearchInput() {
    this.searchFormControl.setValue('');
    this.nodeList = [];
  }

  private searchProcessObserver() {
    const refModel = this.rmeStore.refModel;

    const sfType = this.column === 'parent_object' ? 'Object' : 'Managed Package';

    this.searchFormControl.valueChanges
      .pipe(
        this.takeUntilComponentDestroy(),
        debounceTime(400),
        distinctUntilChanged(),
        map(value => value.trim()),
        filter(value => {
          return value.length >= constants.searchValidation.defaultMinL;
        }),
        switchMap(searchValue => {
          return this.searchNodeObserver$(searchValue, refModel._id, sfType);
        })
      )
      .subscribe();
  }

  private searchNodeObserver$(searchValue, refModelId, sfType) {
    this.loading$.next(true);
    return this.rmeNodeDaoService.searchNode(searchValue, refModelId, sfType, NODES_LIMIT).pipe(
      this.takeUntilComponentDestroy(),
      tap((data: SearchResult) => {
        this.result = data;
        this.nodeList = data.refNodes;
        this._filterWithExistingParents();
        this.changeDetectorRef.detectChanges();
      }),
      finalize(() => {
        this.loading$.next(false);
      }),
      catchError(() => {
        this.loading$.next(false);
        this.result = null;

        return EMPTY;
      })
    );
  }

  private _filterWithExistingParents() {
    this.nodeList = this.nodeList.filter(node => {
      return !this.addedParents.find(parent => parent.id === node._id);
    });
  }

  addParent(node: any) {
    this.addParentNode.emit({id: node._id, parentName: node.name, parentIdentity: node.identity});

    this.clearSearchInput();
    this._changeInputState();
  }

  _changeInputState() {
    this.disableSearch = this.addedParents?.length === PARENTS_LIMIT;
  }
}
